import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { getSupabaseClient, isSupabaseConfigured, checkDatabaseHealth, testDatabaseConnection } from '../utils/supabase'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isConfigured, setIsConfigured] = useState(false)
  const [databaseHealthy, setDatabaseHealthy] = useState(true)
  const [databaseError, setDatabaseError] = useState(null)
  const [isCheckingDatabase, setIsCheckingDatabase] = useState(false)

  // Check database health
  const checkDatabase = useCallback(async () => {
    const configCheck = isSupabaseConfigured()
    if (!configCheck) {
      setDatabaseHealthy(false)
      setDatabaseError('Database not configured')
      return
    }

    setIsCheckingDatabase(true)
    setDatabaseError(null)

    try {
      const health = await checkDatabaseHealth()
      setDatabaseHealthy(health.isHealthy)
      setDatabaseError(health.error)
    } catch (error) {
      setDatabaseHealthy(false)
      setDatabaseError('Database health check failed')
    } finally {
      setIsCheckingDatabase(false)
    }
  }, [])

  // Test database connection with retry
  const testDatabase = useCallback(async () => {
    const configCheck = isSupabaseConfigured()
    if (!configCheck) {
      setDatabaseHealthy(false)
      setDatabaseError('Database not configured')
      return
    }

    setIsCheckingDatabase(true)
    setDatabaseError(null)

    try {
      const result = await testDatabaseConnection()
      setDatabaseHealthy(result.success)
      setDatabaseError(result.success ? null : result.error)
    } catch (error) {
      setDatabaseHealthy(false)
      setDatabaseError('Database connection test failed')
    } finally {
      setIsCheckingDatabase(false)
    }
  }, [])

  useEffect(() => {
    const configCheck = isSupabaseConfigured()
    setIsConfigured(configCheck)
    
    if (!configCheck) {
      setLoading(false)
      setDatabaseHealthy(false)
      setDatabaseError('Database not configured')
      return
    }

    // Get initial session
    let subscription = null
    try {
      const client = getSupabaseClient()
      client.auth.getSession().then(({ data: { session } }) => {
        setUser(session?.user ?? null)
        setLoading(false)
      })

      // Listen for auth changes
      const { data: { subscription: authSubscription } } = client.auth.onAuthStateChange(
        async (event, session) => {
          setUser(session?.user ?? null)
          setLoading(false)
        }
      )
      subscription = authSubscription
    } catch (error) {
      console.warn('Failed to initialize auth:', error.message)
      setLoading(false)
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe()
      }
    }
  }, []) // Remove checkDatabase dependency to prevent infinite loop

  // Check database health after initial setup
  useEffect(() => {
    if (isConfigured) {
      checkDatabase()
    }
  }, [isConfigured, checkDatabase])

  // Periodic database health check (every 30 seconds)
  useEffect(() => {
    if (!isConfigured || !databaseHealthy) return

    const interval = setInterval(() => {
      checkDatabase()
    }, 30000) // 30 seconds

    return () => clearInterval(interval)
  }, [isConfigured, databaseHealthy, checkDatabase])

  const signInWithGoogle = async () => {
    if (!isConfigured) return { error: new Error('Supabase not configured') }
    
    try {
      const client = getSupabaseClient()
      const { data, error } = await client.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
        }
      })
      return { data, error }
    } catch (err) {
      return { error: err }
    }
  }

  const signInWithGitHub = async () => {
    if (!isConfigured) return { error: new Error('Supabase not configured') }
    
    try {
      const client = getSupabaseClient()
      const { data, error } = await client.auth.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo: window.location.origin
        }
      })
      return { data, error }
    } catch (err) {
      return { error: err }
    }
  }

  const signOut = async () => {
    if (!isConfigured) return { error: new Error('Supabase not configured') }
    
    try {
      const client = getSupabaseClient()
      const { error } = await client.auth.signOut()
      return { error }
    } catch (err) {
      return { error: err }
    }
  }

  const value = {
    user,
    loading,
    isConfigured,
    databaseHealthy,
    databaseError,
    isCheckingDatabase,
    checkDatabase,
    testDatabase,
    signInWithGoogle,
    signInWithGitHub,
    signOut
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
